/**
 * Detector de atividade de voz (VAD) por energia, com piso de ruído adaptativo.
 *
 * É PURO de propósito: recebe nível e tempo como argumentos em vez de ler o relógio ou o
 * AnalyserNode. Isso permite testar minutos de conversa em milissegundos, sem browser — e é
 * onde mora toda a lógica que antes estava espalhada no voice-hotword.service.
 */
export interface VadConfig {
  /** Limiar de início = noiseFloor × este fator. */
  fatorInicio: number;
  /** Limiar para manter a fala viva (mais permissivo que o de início). */
  fatorManter: number;
  /** Limiar durante a fala da IA — mais alto, para o eco residual não disparar barge-in. */
  fatorDuranteIaFalando: number;
  /** Guarda para sala quase muda, onde um floor minúsculo tornaria o limiar relativo instável. */
  pisoAbsoluto: number;
  /** Silêncio contínuo que encerra a fala. */
  silencioMs: number;
  /** Abaixo disto a fala é considerada ruído e descartada. */
  falaMinimaMs: number;
  /** Acima disto a fala é encerrada e ENVIADA (não descartada). */
  duracaoMaximaMs: number;
  /** Margem somada ao fim da fala para não cortar a última sílaba. */
  margemFimMs: number;
  /** No arranque do TTS, ignora tudo — o próprio início da fala da IA não pode se auto-disparar. */
  janelaMortaTtsMs: number;
  /** Peso da média móvel do piso de ruído (0..1). 0.05 ≈ 400 ms de adaptação a 20 ms/quadro. */
  alfaFloor: number;
}

export const VAD_CONFIG_PADRAO: VadConfig = {
  fatorInicio: 3.0,
  fatorManter: 1.8,
  fatorDuranteIaFalando: 4.5,
  pisoAbsoluto: 0.02,
  silencioMs: 1600,
  falaMinimaMs: 400,
  duracaoMaximaMs: 15000,
  margemFimMs: 200,
  janelaMortaTtsMs: 500,
  alfaFloor: 0.05,
};

export type VadEvento =
  | { tipo: 'fala-comecou'; tMs: number }
  /** `fimMs` é a última voz + margem — não o instante do timeout, senão o áudio
   *  enviado carregaria os 1600 ms de silêncio final. */
  | { tipo: 'fala-terminou'; inicioMs: number; fimMs: number }
  | { tipo: 'fala-descartada'; motivo: 'curta' }
  | { tipo: 'barge-in'; tMs: number };

export class VadDetector {
  private floor = 0;
  private floorIniciado = false;
  private falando = false;
  private inicioMs = 0;
  private ultimaVozMs = 0;
  private ttsInicioMs: number | null = null;

  constructor(private readonly cfg: VadConfig) {}

  get noiseFloor(): number {
    return this.floor;
  }

  /**
   * Limiar que dispara "fala-comecou" com o piso de ruído ATUAL.
   *
   * Fonte ÚNICA da fórmula: o serviço de captura e o painel de diagnóstico apenas leem daqui.
   * Quando isto vivia duplicado no voice-hotword.service, a UI podia mostrar um limiar
   * diferente do que o detector realmente usava.
   */
  get limiarInicio(): number {
    return Math.max(this.floor * this.cfg.fatorInicio, this.cfg.pisoAbsoluto);
  }

  reiniciar(): void {
    this.falando = false;
    this.inicioMs = 0;
    this.ultimaVozMs = 0;
    this.ttsInicioMs = null;
  }

  processar(nivel: number, tMs: number, iaFalando: boolean): VadEvento[] {
    const eventos: VadEvento[] = [];

    // Marca o arranque do TTS para a janela morta.
    if (iaFalando && this.ttsInicioMs === null) this.ttsInicioMs = tMs;
    if (!iaFalando) this.ttsInicioMs = null;

    // O piso só aprende com silêncio real: durante a fala (sua ou da IA) a medida está poluída.
    if (!this.falando && !iaFalando) {
      this.floor = this.floorIniciado
        ? this.floor + this.cfg.alfaFloor * (nivel - this.floor)
        : nivel;
      this.floorIniciado = true;
    }

    if (!this.falando) {
      if (iaFalando) {
        const desdeTts = this.ttsInicioMs === null ? 0 : tMs - this.ttsInicioMs;
        if (desdeTts < this.cfg.janelaMortaTtsMs) return eventos;

        // Limiar mais alto que o normal: só interrompe a IA fala claramente acima do eco residual.
        const limiarBarge = Math.max(
          this.floor * this.cfg.fatorDuranteIaFalando,
          this.cfg.pisoAbsoluto,
        );
        if (nivel >= limiarBarge) {
          this.falando = true;
          this.inicioMs = tMs;
          this.ultimaVozMs = tMs;
          eventos.push({ tipo: 'barge-in', tMs });
          eventos.push({ tipo: 'fala-comecou', tMs });
        }
        return eventos;
      }
      if (nivel >= this.limiarInicio) {
        this.falando = true;
        this.inicioMs = tMs;
        this.ultimaVozMs = tMs;
        eventos.push({ tipo: 'fala-comecou', tMs });
      }
      return eventos;
    }

    // Falando: renova enquanto houver voz.
    if (nivel >= this.floor * this.cfg.fatorManter) this.ultimaVozMs = tMs;

    if (tMs - this.inicioMs >= this.cfg.duracaoMaximaMs) {
      eventos.push(this.encerrar());
      // Fala contínua: se este quadro ainda é claramente voz, reabre a fala no mesmo
      // quadro. Sem isso, o próximo quadro cairia no ramo "não falando" com o nível
      // ainda alto e poluiria o noiseFloor com a própria voz do usuário — o piso subiria,
      // os limiares (inclusive o de barge-in) subiriam junto, e o detector ficaria surdo.
      if (nivel >= this.limiarInicio) {
        this.falando = true;
        this.inicioMs = tMs;
        this.ultimaVozMs = tMs;
        eventos.push({ tipo: 'fala-comecou', tMs });
      }
      return eventos;
    }

    if (tMs - this.ultimaVozMs >= this.cfg.silencioMs) {
      eventos.push(this.encerrar());
    }
    return eventos;
  }

  /** Fecha a fala corrente decidindo entre enviar e descartar. */
  private encerrar(): VadEvento {
    const inicioMs = this.inicioMs;
    const duracao = this.ultimaVozMs - inicioMs;
    this.falando = false;
    if (duracao < this.cfg.falaMinimaMs) {
      return { tipo: 'fala-descartada', motivo: 'curta' };
    }
    return { tipo: 'fala-terminou', inicioMs, fimMs: this.ultimaVozMs + this.cfg.margemFimMs };
  }
}
